import React, { useState } from "react";
import {
    Box,
    Button,
    useToast,
    Text,
    InputGroup,
    InputLeftElement,
    HStack,
    VStack,
    Flex,
    Select,
    Icon,
    Avatar,
    Input,
    FormControl,
    FormLabel,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Checkbox,
    CheckboxGroup,
    Badge,
    Divider,
    SimpleGrid,
    Tooltip,
} from "@chakra-ui/react";
import {
    MdSearch,
    MdCheckCircle,
    MdLocationOn,
} from "react-icons/md";
import { bulkAssignUsersToSite } from "../../../../api/api";

const BulkSiteAssignmentModal = ({
    isOpen,
    onClose,
    sites,
    allUsers,
    onSuccess
}) => {
    const [selectedBulkUserIds, setSelectedBulkUserIds] = useState([]);
    const [bulkSelectedSiteId, setBulkSelectedSiteId] = useState("");
    const [bulkAssignmentStartDate, setBulkAssignmentStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState("");
    const toast = useToast();

    const handleBulkAssign = async () => {
        try {
            if (!bulkSelectedSiteId || selectedBulkUserIds.length === 0 || !bulkAssignmentStartDate) {
                toast({ title: "Veuillez remplir tous les champs", status: "warning", isClosable: true });
                return;
            }

            setIsBulkProcessing(true);

            const res = await bulkAssignUsersToSite({
                UserIDs: selectedBulkUserIds,
                SiteID: bulkSelectedSiteId,
                StartDate: bulkAssignmentStartDate
            });

            toast({
                title: "Assignation terminée",
                description: `${res.data.completed} utilisateur(s) assigné(s) avec succès. ${res.data.skipped > 0 ? `${res.data.skipped} ignoré(s).` : ''}`,
                status: "success",
                duration: 5000,
                isClosable: true
            });

            if (onSuccess) onSuccess();

            resetForm();
            onClose();
        } catch (err) {
            console.error("Error in bulk processing:", err);
            toast({ title: "Erreur lors de l'assignation", description: err.response?.data?.error || err.message, status: "error", isClosable: true });
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const resetForm = () => {
        setSelectedBulkUserIds([]);
        setBulkSelectedSiteId("");
        setBulkAssignmentStartDate(new Date().toISOString().split('T')[0]);
        setUserSearchQuery("");
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="20px" boxShadow="0 20px 40px rgba(0,0,0,0.1)">
                <ModalHeader borderBottom="1px solid" borderColor="gray.100" pb={4}>
                    <VStack align="start" spacing={1}>
                        <Text fontSize="xl" fontWeight="700" color="secondaryGray.900">
                            Assignation d'équipe
                        </Text>
                        <Text fontSize="sm" fontWeight="400" color="gray.500">
                            Sélectionnez les membres de l'équipe à assigner à un site spécifique.
                        </Text>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton borderRadius="full" top="20px" />

                <ModalBody py={6}>
                    <VStack spacing={6} align="stretch">
                        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Destination</FormLabel>
                                <Select
                                    placeholder="Choisir un site"
                                    value={bulkSelectedSiteId}
                                    onChange={(e) => setBulkSelectedSiteId(e.target.value)}
                                    borderRadius="10px"
                                    bg="gray.50"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    _focus={{ borderColor: "blue.400", bg: "white" }}
                                >
                                    {sites.map((site) => (
                                        <option key={site.SiteID} value={site.SiteID}>
                                            {site.SiteName}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </SimpleGrid>

                        <Divider />

                        <VStack align="stretch" spacing={3}>
                            <Flex justify="space-between" align="center">
                                <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={0}>
                                    Membres ({selectedBulkUserIds.length} sélectionnés)
                                </FormLabel>
                                <InputGroup size="sm" maxW="200px">
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={MdSearch} color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Chercher..."
                                        borderRadius="full"
                                        bg="gray.50"
                                        value={userSearchQuery}
                                        onChange={(e) => setUserSearchQuery(e.target.value)}
                                    />
                                </InputGroup>
                            </Flex>

                            <Box
                                maxH="350px"
                                overflowY="auto"
                                p={2}
                                border="1px solid"
                                borderColor="gray.100"
                                borderRadius="15px"
                                bg="gray.50"
                            >
                                <CheckboxGroup
                                    colorScheme="blue"
                                    value={selectedBulkUserIds}
                                    onChange={(values) => setSelectedBulkUserIds(values)}
                                >
                                    <VStack spacing={1} align="stretch">
                                        {allUsers
                                            .filter(u => ['infirmier', 'ambulancier'].includes(u.rôle?.toLowerCase()))
                                            .filter(u => {
                                                const fullName = `${u.prenom} ${u.nom}`.toLowerCase();
                                                return fullName.includes(userSearchQuery.toLowerCase());
                                            })
                                            .map((user) => (
                                                <Box
                                                    key={user.id}
                                                    p={3}
                                                    borderRadius="12px"
                                                    _hover={{ bg: "white", boxShadow: "sm", transform: "translateY(-1px)" }}
                                                    transition="all 0.2s"
                                                    bg={selectedBulkUserIds.includes(user.id.toString()) ? "white" : "transparent"}
                                                    boxShadow={selectedBulkUserIds.includes(user.id.toString()) ? "sm" : "none"}
                                                >
                                                    <Checkbox
                                                        value={user.id.toString()}
                                                        w="100%"
                                                        sx={{
                                                            ".chakra-checkbox__control": {
                                                                borderRadius: "4px",
                                                                _checked: { bg: "#29BACD", borderColor: "#29BACD" }
                                                            }
                                                        }}
                                                    >
                                                        <HStack spacing={3} ml={1}>
                                                            <Avatar
                                                                size="sm"
                                                                name={`${user.prenom} ${user.nom}`}
                                                                src={user.photo}
                                                                borderRadius="8px"
                                                            />
                                                            <VStack align="start" spacing={0} flex="1">
                                                                <HStack spacing={2}>
                                                                    <Text fontSize="sm" fontWeight="600" color="gray.700">
                                                                        {user.prenom} {user.nom}
                                                                    </Text>
                                                                    {user.site && (
                                                                        <Badge
                                                                            fontSize="10px"
                                                                            colorScheme="orange"
                                                                            variant="solid"
                                                                            borderRadius="4px"
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap={1}
                                                                        >
                                                                            <Icon as={MdLocationOn} />
                                                                            {user.site.name}
                                                                        </Badge>
                                                                    )}
                                                                </HStack>
                                                                <Badge
                                                                    fontSize="10px"
                                                                    px={2}
                                                                    borderRadius="full"
                                                                    colorScheme={user.rôle?.toLowerCase() === 'infirmier' ? 'purple' : 'teal'}
                                                                    variant="subtle"
                                                                >
                                                                    {user.rôle}
                                                                </Badge>
                                                            </VStack>
                                                            {user.site && (
                                                                <Tooltip label={`Actuellement assigné à ${user.site.name}`} placement="top">
                                                                    <Box>
                                                                        <Icon as={MdLocationOn} color="orange.400" />
                                                                    </Box>
                                                                </Tooltip>
                                                            )}
                                                        </HStack>
                                                    </Checkbox>
                                                </Box>
                                            ))}
                                    </VStack>
                                </CheckboxGroup>
                            </Box>
                        </VStack>
                    </VStack>
                </ModalBody>
                <ModalFooter borderTop="1px solid" borderColor="gray.100" bg="gray.50" borderBottomRadius="20px">
                    <HStack spacing={3}>
                        <Button
                            variant="ghost"
                            onClick={onClose}
                            isDisabled={isBulkProcessing}
                            borderRadius="10px"
                        >
                            Annuler
                        </Button>
                        <Button
                            bg="#29BACD"
                            color="white"
                            _hover={{ bg: "#219ebc", transform: "translateY(-1px)", boxShadow: "lg" }}
                            _active={{ bg: "#1a759f" }}
                            onClick={handleBulkAssign}
                            isLoading={isBulkProcessing}
                            isDisabled={!bulkSelectedSiteId || selectedBulkUserIds.length === 0 || !bulkAssignmentStartDate}
                            borderRadius="10px"
                            px={8}
                            leftIcon={<Icon as={MdCheckCircle} />}
                        >
                            Assigner {selectedBulkUserIds.length > 0 && `(${selectedBulkUserIds.length})`}
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default BulkSiteAssignmentModal;
